import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Lock, ScanFace, Clock, X, Check } from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';
import { api, ApiError } from '../lib/api';
import { useToast } from '../components/Toast';
import { PinPad } from '../components/PinPad';
import { biometricLabel, platformAuthenticatorAvailable, registerBiometric, webauthnSupported } from '../lib/webauthn';

const PIN_LEN = 6;
const AUTO_LOCK_OPTIONS: { value: number; label: string }[] = [
  { value: 0, label: 'Immediato' },
  { value: 60, label: 'Dopo 1 minuto' },
  { value: 300, label: 'Dopo 5 minuti' },
  { value: -1, label: 'Mai' },
];

export function autoLockLabel(seconds: number): string {
  return AUTO_LOCK_OPTIONS.find((o) => o.value === seconds)?.label ?? 'Dopo 1 minuto';
}

export function SicurezzaScreen() {
  const navigate = useNavigate();
  const { user, applyMe } = useAuth();
  const { show } = useToast();
  const sec = user?.security;

  const [bioAvailable, setBioAvailable] = useState(false);
  const [pinFlow, setPinFlow] = useState(false);
  const [lockSheet, setLockSheet] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void platformAuthenticatorAvailable().then(setBioAvailable);
  }, []);

  if (!sec) return <div className="screen">Caricamento…</div>;

  const protectedOn = sec.pin_set || sec.biometric_enabled;
  const heroSub = sec.biometric_enabled
    ? `Sblocco con ${biometricLabel()} attivo`
    : sec.pin_set
      ? 'Sblocco con PIN attivo'
      : 'Nessun blocco attivo';

  const toggleBiometric = async () => {
    if (!bioAvailable || busy) return;
    setBusy(true);
    try {
      if (sec.biometric_enabled) {
        applyMe(await api.updateSecurity({ biometric_enabled: false }));
        show('Sblocco biometrico disattivato');
      } else {
        let credJson: string | undefined;
        if (!sec.biometric_registered) {
          credJson = await registerBiometric(user!.id, user!.display_name ?? user!.email);
          // La credenziale è device-local: la salvo anche localmente per la lock screen.
          localStorage.setItem('archivio.webauthn', credJson);
        }
        applyMe(await api.updateSecurity({ biometric_enabled: true, webauthn_cred_json: credJson }));
        show('Sblocco biometrico attivato');
      }
    } catch (e) {
      show(e instanceof Error && e.message ? e.message : 'Operazione non riuscita');
    } finally {
      setBusy(false);
    }
  };

  const setAutoLock = async (value: number) => {
    setLockSheet(false);
    try {
      applyMe(await api.updateSecurity({ auto_lock_seconds: value }));
      show('Blocco automatico aggiornato');
    } catch {
      show('Aggiornamento non riuscito');
    }
  };

  return (
    <div className="screen">
      <div className="topbar">
        <button className="iconbtn" onClick={() => navigate(-1)} aria-label="Indietro">
          <ChevronLeft size={20} />
        </button>
        <div className="topbar__title">Sicurezza</div>
        <span style={{ width: 40 }} />
      </div>

      {/* Hero stato lock */}
      <div className="lockhero" style={{ marginBottom: 22 }}>
        <span className="lockhero__icon">
          <Lock size={24} />
        </span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 700 }}>{protectedOn ? 'Archivio protetto' : 'Archivio non protetto'}</div>
          <div style={{ fontSize: 12, color: 'var(--adim)', marginTop: 2 }}>{heroSub}</div>
        </div>
      </div>

      {/* Sblocco biometrico */}
      <div className="eyebrow" style={{ marginBottom: 11 }}>Sblocco biometrico</div>
      <div className="card-pad" style={{ marginBottom: 22 }}>
        <div className="optionrow">
          <ScanFace size={20} style={{ color: 'var(--accent)' }} />
          <div className="optionrow__body">
            <div className="optionrow__title">{biometricLabel()}</div>
            <div className="optionrow__sub">
              {bioAvailable
                ? "Sblocca l'app al riavvio"
                : webauthnSupported()
                  ? 'Non disponibile su questo device'
                  : 'Browser non compatibile'}
            </div>
          </div>
          <button
            type="button"
            className={`switch${sec.biometric_enabled ? ' is-on' : ''}`}
            onClick={toggleBiometric}
            disabled={!bioAvailable || busy}
            aria-label={biometricLabel()}
            aria-pressed={sec.biometric_enabled}
          >
            <span className="switch__knob" />
          </button>
        </div>
      </div>

      {/* Codice PIN */}
      <div className="eyebrow" style={{ marginBottom: 11 }}>Codice PIN</div>
      <div className="card" style={{ padding: 16, marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <span style={{ fontSize: 14, fontWeight: 600 }}>{sec.pin_set ? 'PIN attuale' : 'Nessun PIN impostato'}</span>
          {sec.pin_set && (
            <span className="pindots">
              {Array.from({ length: PIN_LEN }).map((_, i) => (
                <span key={i} className="is-filled" />
              ))}
            </span>
          )}
        </div>
        <button type="button" className="cardaction" onClick={() => setPinFlow(true)}>
          <span>{sec.pin_set ? 'Cambia codice PIN' : 'Imposta codice PIN'}</span>
          <ChevronRight size={16} />
        </button>
      </div>

      {/* Blocco automatico */}
      <button
        type="button"
        className="card"
        style={{ display: 'flex', alignItems: 'center', gap: 13, padding: '14px 15px', width: '100%', textAlign: 'left' }}
        onClick={() => setLockSheet(true)}
      >
        <Clock size={18} style={{ color: 'var(--accent)' }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>Blocco automatico</div>
          <div style={{ fontSize: 11, color: 'var(--muted)' }}>{autoLockLabel(sec.auto_lock_seconds)}</div>
        </div>
        <ChevronRight size={15} style={{ color: 'var(--faint)' }} />
      </button>

      {pinFlow && (
        <PinChangeFlow
          hasPin={sec.pin_set}
          onClose={() => setPinFlow(false)}
          onDone={async () => {
            setPinFlow(false);
            try {
              applyMe(await api.me());
            } catch {
              /* ignora */
            }
            show('PIN aggiornato');
          }}
        />
      )}

      {lockSheet && (
        <Sheet title="Blocco automatico" onClose={() => setLockSheet(false)}>
          {AUTO_LOCK_OPTIONS.map((o) => (
            <button key={o.value} type="button" className="setting" onClick={() => setAutoLock(o.value)}>
              <span className="setting__label">{o.label}</span>
              {sec.auto_lock_seconds === o.value && <Check size={18} style={{ color: 'var(--accent)' }} />}
            </button>
          ))}
        </Sheet>
      )}
    </div>
  );
}

// --- Flusso cambio/imposta PIN: (attuale →) nuovo → conferma ---
function PinChangeFlow({ hasPin, onClose, onDone }: { hasPin: boolean; onClose: () => void; onDone: () => void }) {
  const { show } = useToast();
  const [step, setStep] = useState<'current' | 'new' | 'confirm'>(hasPin ? 'current' : 'new');
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [stepKey, setStepKey] = useState(0); // forza reset del PinPad

  const reset = (s: 'current' | 'new' | 'confirm') => {
    setStep(s);
    setError(null);
    setStepKey((k) => k + 1);
  };

  const handle = async (pin: string) => {
    if (step === 'current') {
      try {
        const { ok } = await api.verifyPin(pin);
        if (!ok) {
          setError('PIN attuale errato');
          setStepKey((k) => k + 1);
          return;
        }
        setCurrent(pin);
        reset('new');
      } catch {
        setError('Verifica non riuscita');
        setStepKey((k) => k + 1);
      }
      return;
    }
    if (step === 'new') {
      setNext(pin);
      reset('confirm');
      return;
    }
    // confirm
    if (pin !== next) {
      setError('I PIN non coincidono');
      reset('new');
      return;
    }
    try {
      await api.setPin(next, hasPin ? current : undefined);
      onDone();
    } catch (e) {
      show(e instanceof ApiError ? e.message : 'Salvataggio PIN non riuscito');
      onClose();
    }
  };

  const title = step === 'current' ? 'Inserisci il PIN attuale' : step === 'new' ? 'Nuovo PIN' : 'Conferma il PIN';

  return (
    <div className="lockscreen">
      <button className="iconbtn" style={{ position: 'absolute', top: 20, right: 20 }} onClick={onClose} aria-label="Chiudi">
        <X size={20} />
      </button>
      <span className="lockscreen__icon">
        <Lock size={30} />
      </span>
      <div style={{ fontSize: 18, fontWeight: 700 }}>{title}</div>
      <PinPad key={stepKey} length={PIN_LEN} onComplete={handle} error={error} />
    </div>
  );
}

function Sheet({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'flex-end' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="card"
        style={{ width: '100%', maxWidth: 560, margin: '0 auto', borderRadius: 'var(--rcard) var(--rcard) 0 0', padding: '8px 18px 28px' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0' }}>
          <span className="eyebrow">{title}</span>
          <button className="iconbtn" onClick={onClose} aria-label="Chiudi" style={{ width: 32, height: 32 }}>
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
